import { BufferGeometry, Float32BufferAttribute } from 'three';
export interface MetalRegion { fromMm: number; toMm: number; depthMm: number; transitionMm?: number; yMinMm?: number; yMaxMm?: number }
/** Split triangles at every boundary BEFORE assigning material or changing depth.
 * Coordinates are local mm before wrapping or temple rotation. Input remains owned by caller.
 * Continuous depth ramps preserve a watertight surface without artificial caps at joints. */
export function hybridRegions(source: BufferGeometry, regions: readonly MetalRegion[], metalIndex: number, photoMetalIndex?: number): BufferGeometry {
  const input = source.index ? source.toNonIndexed() : source;
  const names = Object.keys(input.attributes);
  const p = input.getAttribute('position');
  let zMin=Infinity,zMax=-Infinity;
  for(let i=0;i<p.count;i++){zMin=Math.min(zMin,p.getZ(i));zMax=Math.max(zMax,p.getZ(i));}
  const depth=zMax-zMin;
  const sorted=[...regions].sort((a,b)=>a.fromMm-b.fromMm);
  for(let i=0;i<sorted.length;i++) {
    const r=sorted[i]!;
    if(![r.fromMm,r.toMm,r.depthMm,r.transitionMm??.5].every(Number.isFinite) || r.fromMm>=r.toMm || r.depthMm<=0 || r.depthMm>depth || (r.transitionMm??.5)<=0 || (i>0 && sorted[i-1]!.toMm>r.fromMm)) throw new Error('Invalid metal region');
    if ((r.yMinMm !== undefined || r.yMaxMm !== undefined) && (!Number.isFinite(r.yMinMm) || !Number.isFinite(r.yMaxMm) || r.yMinMm! >= r.yMaxMm!)) throw new Error('Invalid metal region Y bounds');
  }
  type Vertex=Record<string,number[]>;
  // Emit one contiguous draw group per material, not one group per clipped triangle.
  const buckets = new Map<number, Record<string,number[]>>();
  const out=new BufferGeometry();
  const read=(i:number):Vertex=>Object.fromEntries(names.map(n=>{const a=input.getAttribute(n);return[n,Array.from({length:a.itemSize},(_,k)=>a.getComponent(i,k))];}));
  const mix=(a:Vertex,b:Vertex,t:number):Vertex=>Object.fromEntries(names.map(n=>[n,a[n]!.map((v,k)=>v+(b[n]![k]!-v)*t)]));
  const clip=(poly:Vertex[],bound:number,greater:boolean,axis=0):Vertex[]=>{
    const result:Vertex[]=[];
    for(let i=0;i<poly.length;i++){
      const a=poly[i]!,b=poly[(i+1)%poly.length]!;
      const da=a.position![axis]!-bound,db=b.position![axis]!-bound;
      const insideA=greater?da>=0:da<=0,insideB=greater?db>=0:db<=0;
      if(insideA)result.push(a);
      if(insideA!==insideB)result.push(mix(a,b,da/(da-db)));
    }return result;
  };
  const cuts=[...new Set(sorted.flatMap(r=>{const t=Math.min(r.transitionMm??.5,(r.toMm-r.fromMm)/2);return[r.fromMm,r.fromMm+t,r.toMm-t,r.toMm];}))].sort((a,b)=>a-b);
  const edges=[-Infinity,...cuts,Infinity];
  const yCuts=[...new Set(sorted.flatMap(r=>{
    if(r.yMinMm===undefined||r.yMaxMm===undefined)return [];
    const t=Math.min(r.transitionMm??.5,(r.toMm-r.fromMm)/2,(r.yMaxMm-r.yMinMm)/2);
    return [r.yMinMm,r.yMinMm+t,r.yMaxMm-t,r.yMaxMm];
  }))].sort((a,b)=>a-b);
  const yEdges=[-Infinity,...yCuts,Infinity];
  const materialAt=(i:number)=>input.groups.find(g=>i>=g.start&&i<g.start+g.count)?.materialIndex??0;
  for(let i=0;i<p.count;i+=3){
    const tri=[read(i),read(i+1),read(i+2)];
    for(let j=0;j<edges.length-1;j++){
      const lo=edges[j]!,hi=edges[j+1]!;let poly=tri;
      if(Number.isFinite(lo))poly=clip(poly,lo,true);
      if(Number.isFinite(hi))poly=clip(poly,hi,false);
      if(poly.length<3)continue;
      const xPoly=poly;
      for(let yi=0;yi<yEdges.length-1;yi++) {
      const yLo=yEdges[yi]!,yHi=yEdges[yi+1]!;
      poly=xPoly;
      if(Number.isFinite(yLo))poly=clip(poly,yLo,true,1);
      if(Number.isFinite(yHi))poly=clip(poly,yHi,false,1);
      if(poly.length<3)continue;
      const mid=Number.isFinite(lo)&&Number.isFinite(hi)?(lo+hi)/2:NaN;
      const midY=poly.reduce((sum,v)=>sum+v.position![1]!,0)/poly.length;
      const region=sorted.find(r=>mid>r.fromMm&&mid<r.toMm&&(r.yMinMm===undefined||midY>r.yMinMm&&midY<r.yMaxMm!));
      const originalMaterial=materialAt(i);
      const mat=region?(photoMetalIndex!==undefined && originalMaterial===0 ? photoMetalIndex : metalIndex):originalMaterial;
      let values = buckets.get(mat);
      if (!values) { values = Object.fromEntries(names.map(n => [n, [] as number[]])); buckets.set(mat, values); }
      for(let k=1;k<poly.length-1;k++){
        const verts=[poly[0]!,poly[k]!,poly[k+1]!];
        const a=verts[0]!.position!,b=verts[1]!.position!,c=verts[2]!.position!;
        const u=b.map((v,q)=>v-a[q]!),v=c.map((v,q)=>v-a[q]!);
        if(Math.hypot(u[1]!*v[2]!-u[2]!*v[1]!,u[2]!*v[0]!-u[0]!*v[2]!,u[0]!*v[1]!-u[1]!*v[0]!)<1e-10)continue;
        for(const vertex of verts){
          const x=vertex.position![0]!;
          const y=vertex.position![1]!;
          let factor=1,slope=0,slopeY=0;
          // A boundary vertex belongs to the already-clipped triangle's
          // region. An inclusive point lookup also assigned the metal ramp's
          // derivative to adjacent untouched acetate, flipping its normals.
          const r=region;
          if(r && r.yMinMm===undefined){const t=Math.min(r.transitionMm??.5,(r.toMm-r.fromMm)/2);const w=Math.min(1,(x-r.fromMm)/t,(r.toMm-x)/t);factor=1-w*(1-r.depthMm/depth);slope=(x<r.fromMm+t?-1:x>r.toMm-t?1:0)*(1-r.depthMm/depth)/t;}
          else if(r){
            const t=Math.min(r.transitionMm??.5,(r.toMm-r.fromMm)/2,r.yMinMm===undefined?Infinity:(r.yMaxMm!-r.yMinMm)/2);
            const margins=[{v:1,dx:0,dy:0},{v:(x-r.fromMm)/t,dx:1/t,dy:0},{v:(r.toMm-x)/t,dx:-1/t,dy:0}];
            if(r.yMinMm!==undefined)margins.push({v:(y-r.yMinMm)/t,dx:0,dy:1/t},{v:(r.yMaxMm!-y)/t,dx:0,dy:-1/t});
            const edge=margins.reduce((a,b)=>b.v<a.v?b:a);const strength=1-r.depthMm/depth;
            factor=1-edge.v*strength;slope=-edge.dx*strength;slopeY=-edge.dy*strength;
          }
          for(const n of names){const arr=[...vertex[n]!];if(n==='position')arr[2]=(zMin+zMax)/2+(arr[2]!-(zMin+zMax)/2)*factor;if(n==='normal'){arr[0]=arr[0]!-arr[2]!*(vertex.position![2]!-(zMin+zMax)/2)*slope/factor;arr[1]=arr[1]!-arr[2]!*(vertex.position![2]!-(zMin+zMax)/2)*slopeY/factor;arr[2]=arr[2]!/factor;const len=Math.hypot(...arr)||1;for(let q=0;q<arr.length;q++)arr[q]=arr[q]!/len;}values[n]!.push(...arr);}
        }
      }

    }
  }
  }
  const merged: Record<string,number[]> = Object.fromEntries(names.map(n => [n, [] as number[]]));
  let start=0;
  for (const [mat, values] of [...buckets].sort(([a],[b]) => a-b)) {
    const count=values.position!.length/3;
    if (!count) continue;
    for (const n of names) for (const v of values[n]!) merged[n]!.push(v);
    out.addGroup(start,count,mat); start+=count;
  }
  for(const n of names)out.setAttribute(n,new Float32BufferAttribute(merged[n]!,input.getAttribute(n).itemSize));
  // A bounded region's depth transition is a narrow piecewise planar shoulder.
  // Normals from the original curved acetate bevel do not describe that shoulder:
  // use its actual emitted facet normals (only within these small transitions).
  if (sorted.some(r=>r.yMinMm!==undefined)) {
    const positions=out.getAttribute('position'), normals=out.getAttribute('normal');
    const shoulders=new Float32Array(positions.count);
    out.setAttribute('hybridShoulder',new Float32BufferAttribute(shoulders,1));
    if(normals)for(let i=0;i<positions.count;i+=3){
      const cx=(positions.getX(i)+positions.getX(i+1)+positions.getX(i+2))/3;
      const cy=(positions.getY(i)+positions.getY(i+1)+positions.getY(i+2))/3;
      const shoulder=sorted.some(r=>{
        if(r.yMinMm===undefined||cx<r.fromMm||cx>r.toMm||cy<r.yMinMm||cy>r.yMaxMm!)return false;
        const t=Math.min(r.transitionMm??.5,(r.toMm-r.fromMm)/2,(r.yMaxMm!-r.yMinMm)/2);
        return cx<r.fromMm+t||cx>r.toMm-t||cy<r.yMinMm+t||cy>r.yMaxMm!-t;
      });
      if(!shoulder)continue;
      const flag=out.getAttribute('hybridShoulder');for(let k=0;k<3;k++)flag.setX(i+k,1);
      const ax=positions.getX(i+1)-positions.getX(i),ay=positions.getY(i+1)-positions.getY(i),az=positions.getZ(i+1)-positions.getZ(i);
      const bx=positions.getX(i+2)-positions.getX(i),by=positions.getY(i+2)-positions.getY(i),bz=positions.getZ(i+2)-positions.getZ(i);
      const nx=ay*bz-az*by,ny=az*bx-ax*bz,nz=ax*by-ay*bx,len=Math.hypot(nx,ny,nz);
      if(len>1e-12)for(let k=0;k<3;k++)normals.setXYZ(i+k,nx/len,ny/len,nz/len);
    }
  }
  out.computeBoundingBox();out.computeBoundingSphere();
  if(input!==source)input.dispose();return out;
}

/** Recalculate only newly constructed metal/acétate shoulders after wrap subdivision.
 * Their planar faces are construction geometry, not the original rounded bevel. */
export function finishHybridShoulderNormals(geo:BufferGeometry):void {
 const flag=geo.getAttribute('hybridShoulder');if(!flag)return;
 const p=geo.getAttribute('position'),n=geo.getAttribute('normal');if(!n)return;
 for(let i=0;i<p.count;i+=3){
  if(flag.getX(i)<.5)continue;
  const ax=p.getX(i+1)-p.getX(i),ay=p.getY(i+1)-p.getY(i),az=p.getZ(i+1)-p.getZ(i);
  const bx=p.getX(i+2)-p.getX(i),by=p.getY(i+2)-p.getY(i),bz=p.getZ(i+2)-p.getZ(i);
  const nx=ay*bz-az*by,ny=az*bx-ax*bz,nz=ax*by-ay*bx,len=Math.hypot(nx,ny,nz);
  if(len>1e-12)for(let k=0;k<3;k++)n.setXYZ(i+k,nx/len,ny/len,nz/len);
 }
 n.needsUpdate=true;
}
